import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { Pill } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Skeleton, Stack } from "@mui/material";
import { type ShiftInviteMessageProps } from "@src/appV2/Chat/type";
import { parseShiftInviteChatMessage } from "@src/appV2/Chat/utils";
import { Button } from "@src/appV2/redesign/components/Button";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";

import { ChatActionCard } from "../../redesign/Chat/ChatActionCard";
import { ChatWidgetWrapper } from "../../redesign/Chat/ChatWidgetWrapper";
import { SHIFT_INVITES_FULL_PATH } from "../../redesign/WorkerShifts/path";
import { ShiftInviteAlerts } from "../../redesign/WorkerShifts/ShiftInvites/Alerts";
import { useGetShiftInvite } from "../../redesign/WorkerShifts/ShiftInvites/api/useGetShiftInvite";
import { ShiftInviteItemProvider } from "../../redesign/WorkerShifts/ShiftInvites/context/shiftInviteItemContext";
import { ShiftInviteCardContainer } from "../../redesign/WorkerShifts/ShiftInvites/ShiftInviteCardContainer";
import { ShiftInviteCardSkeleton } from "../../redesign/WorkerShifts/ShiftInvites/ShiftInviteCardSkeleton";

export function ShiftInviteRequestMessage(props: ShiftInviteMessageProps) {
  const history = useHistory();
  const { message } = props;
  const { agentMessage, shiftInviteId, status } = parseShiftInviteChatMessage(props);

  if (!isDefined(agentMessage) || status === ShiftInviteStatus.DELETED) {
    // If we return null ,the message will render as standard sendbird message.
    // This is a custom message but we don't have enough data to render it properly
    return <span />;
  }

  // Group shift invites are sent without any data payload in the Sendbird message
  const isGroupShiftInvite = isEmpty(message.data);
  if (isGroupShiftInvite) {
    return (
      <ChatWidgetWrapper>
        <ChatActionCard
          message={agentMessage}
          pill={
            <Pill
              variant="outlined"
              size="small"
              label="Shift invite"
              backgroundColor={(theme) => theme.palette.background.quaternary}
              color={(theme) => theme.palette.intent?.warning.text}
              outlineColor={(theme) => theme.palette.intent?.warning.border}
              sx={{
                width: "fit-content",
              }}
            />
          }
        >
          <Button
            fullWidth
            variant="contained"
            size="small"
            onClick={() => {
              history.push(SHIFT_INVITES_FULL_PATH);
            }}
          >
            Go to Shift Invites
          </Button>
        </ChatActionCard>
      </ChatWidgetWrapper>
    );
  }

  return (
    <ShiftInviteItemProvider>
      <ShiftInviteMessageContent status={status} shiftInviteId={shiftInviteId} />
    </ShiftInviteItemProvider>
  );
}

interface ShiftInviteMessageContentProps {
  shiftInviteId?: string;
  status?: ShiftInviteStatus;
}

function ShiftInviteMessageContent(props: ShiftInviteMessageContentProps) {
  const { shiftInviteId, status } = props;
  const { data: shiftInviteResponse } = useGetShiftInvite(
    { shiftInviteId },
    { enabled: isDefined(shiftInviteId) }
  );

  const shiftInvite = shiftInviteResponse?.data;
  const facilityName = shiftInvite?.attributes.facility.name;

  if (!isDefined(shiftInvite)) {
    const isCardActionable = status === ShiftInviteStatus.PENDING;
    return (
      <ChatWidgetWrapper>
        <Stack alignItems="center" width="100%">
          {/* Message header */}
          <Box sx={{ paddingY: 5 }}>
            <Skeleton variant="text" width={160} />
          </Box>
          <ShiftInviteCardSkeleton isCardActionable={isCardActionable} />
        </Stack>
      </ChatWidgetWrapper>
    );
  }

  return (
    <ChatWidgetWrapper headerText={`${facilityName} sent you a shift invite`}>
      <ShiftInviteCardContainer shiftInvite={shiftInvite} />
      <ShiftInviteAlerts shiftInvite={shiftInvite} />
    </ChatWidgetWrapper>
  );
}
